import { useRef, useState, useEffect, useMemo, Suspense } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Text, Float, MeshDistortMaterial, MeshWobbleMaterial, Image as DreiImage, MeshReflectorMaterial, useTexture, Sparkles } from '@react-three/drei';
import * as THREE from 'three';
import { collection, getDocs, limit, query } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { Shop } from '../types';
import { useExperienceStore } from '../state/useExperienceStore';
import gsap from 'gsap';

const SafeImage = ({ url, fallbackUrl, boxScale = [1, 1], ...props }: any) => {
  const [validUrl, setValidUrl] = useState<string | null>(null);
  const [aspect, setAspect] = useState(1);

  useEffect(() => {
    let isMounted = true;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = url;
    img.onload = () => {
      if (isMounted) {
        setAspect(img.width / img.height);
        setValidUrl(url);
      }
    };
    img.onerror = () => {
      if (isMounted) {
        console.warn(`SafeImage: Failed to load ${url}, using fallback.`);
        const fallback = fallbackUrl || `https://picsum.photos/seed/fallback/1200/800`;
        const fImg = new Image();
        fImg.crossOrigin = "anonymous";
        fImg.src = fallback;
        fImg.onload = () => {
          if (isMounted) {
            setAspect(fImg.width / fImg.height);
            setValidUrl(fallback);
          }
        };
        fImg.onerror = () => {
          if (isMounted) setValidUrl(fallback);
        };
      }
    };
    return () => { isMounted = false; };
  }, [url, fallbackUrl]);

  // Calculate fit scale within box
  const [boxW, boxH] = boxScale;
  const boxAspect = boxW / boxH;
  let finalScale: [number, number] = [boxW, boxH];
  
  if (aspect > boxAspect) {
    // Image is wider than box
    finalScale = [boxW, boxW / aspect];
  } else {
    // Image is taller than box
    finalScale = [boxH * aspect, boxH];
  }

  if (!validUrl) {
    return (
      <mesh {...props} scale={[...finalScale, 1]}>
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial color="#111" transparent opacity={0.2} />
      </mesh>
    );
  }

  return (
    <Suspense fallback={null}>
      <DreiImage {...props} url={validUrl} scale={finalScale} />
    </Suspense>
  );
};

import { sounds } from '../lib/sound';

const StorePod = ({ shop, position, index, rotation = [0, 0, 0] }: { shop: Shop; position: [number, number, number]; index: number; rotation?: [number, number, number] }) => {
  const groupRef = useRef<THREE.Group>(null!);
  const [hovered, setHovered] = useState(false);
  const selectShop = useExperienceStore((state) => state.selectShop);
  const viewport = useThree((state) => state.viewport);

  const isMobile = viewport.width < 10;
  const responsiveScale = isMobile ? 0.6 : 1;

  useFrame((state) => {
    const time = state.clock.getElapsedTime();
    // Smooth floating - slightly faster
    groupRef.current.position.y = position[1] + Math.sin(time * 0.8 + index) * 0.4;
    // Subtle tilt based on hover
    if (hovered) {
      groupRef.current.rotation.y = rotation[1] + Math.sin(time * 1.5) * 0.15;
      groupRef.current.rotation.x = Math.cos(time * 0.8) * 0.08;
      groupRef.current.scale.setScalar(THREE.MathUtils.lerp(groupRef.current.scale.x, responsiveScale * 1.15, 0.15));
    } else {
      groupRef.current.rotation.y = THREE.MathUtils.lerp(groupRef.current.rotation.y, rotation[1], 0.1);
      groupRef.current.rotation.x = THREE.MathUtils.lerp(groupRef.current.rotation.x, 0, 0.1);
      groupRef.current.scale.setScalar(THREE.MathUtils.lerp(groupRef.current.scale.x, responsiveScale, 0.1));
    }
  });

  return (
    <group ref={groupRef} position={position} rotation={rotation} scale={responsiveScale}>
      <Float speed={hovered ? 6 : 2} rotationIntensity={0.3} floatIntensity={0.6}>
        <mesh
          onPointerOver={() => {
            setHovered(true);
            sounds.play('hover', 0.2);
          }}
          onPointerOut={() => setHovered(false)}
          onClick={(e) => {
            e.stopPropagation();
            sounds.play('select', 0.4);
            selectShop(shop.id);
          }}
        >
          {/* Transparent Glass Background */}
          <planeGeometry args={[5.2, 7.2]} />
          <meshPhysicalMaterial 
            color="#ffffff"
            transparent
            opacity={hovered ? 0.08 : 0.03}
            roughness={0.1}
            metalness={0.8}
            transmission={0.5}
            thickness={1}
          />
          
          {/* Content Image Container */}
          <group position={[0, 0.4, 0.1]}>
            <SafeImage
              url={shop.logoURL || `https://picsum.photos/seed/${shop.id}/1200/800`}
              fallbackUrl={`https://picsum.photos/seed/${shop.id}/1200/800`}
              boxScale={[4.8, 6.2]}
              transparent
              opacity={hovered ? 1 : 0.75}
              toneMapped={false}
            />
          </group>

          {/* Glowing Neon Border - Outer */}
          <mesh position={[0, 0, -0.05]}>
            <planeGeometry args={[5.3, 7.3]} />
            <meshBasicMaterial 
              color="#ffffff" 
              transparent 
              opacity={hovered ? 0.4 : 0.1}
              blending={THREE.AdditiveBlending}
            />
          </mesh>

          {/* Intense Neon Edge (Inner Glow) */}
          {hovered && (
            <mesh position={[0, 0, 0.05]} rotation={[0, 0, Math.PI / 4]}>
              <ringGeometry args={[2.5, 2.55, 4]} />
              <meshBasicMaterial color="#ffffff" transparent opacity={0.8} blending={THREE.AdditiveBlending} />
            </mesh>
          )}

          {/* Structural Frame */}
          <lineSegments position={[0, 0, 0]}>
            <edgesGeometry args={[new THREE.PlaneGeometry(5.2, 7.2)]} />
            <lineBasicMaterial 
              color={hovered ? "#ffffff" : "#444444"} 
              transparent 
              opacity={hovered ? 1 : 0.3} 
              linewidth={2}
            />
          </lineSegments>
        </mesh>

        {/* Editorial Typography */}
        <group position={[-2.4, -3.4, 0.2]}>
          <Text
            fontSize={0.25}
            color="white"
            anchorX="left"
            fontWeight="bold"
            letterSpacing={0.2}
            fillOpacity={hovered ? 0.8 : 0.4}
          >
            {`0${index + 1}`.slice(-2)}
          </Text>
          <Text
            position={[0, -0.45, 0]}
            fontSize={0.5}
            color="white"
            anchorX="left"
            fontWeight="black"
            letterSpacing={-0.02}
            font="https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hjp-Ek-_EeA.woff"
            fillOpacity={hovered ? 1 : 0.6}
          >
            {shop.name.toUpperCase()}
          </Text>
          <Text
            position={[0, -0.85, 0]}
            fontSize={0.2}
            color="white"
            anchorX="left"
            fontWeight="medium"
            letterSpacing={0.1}
            fillOpacity={hovered ? 0.6 : 0.3}
          >
            {`${shop.location?.city || 'Virtual'} • ${shop.categories?.[0] || 'Premium'}`.toUpperCase()}
          </Text>
        </group>
      </Float>
    </group>
  );
};

export const MallHub = () => {
  const [shops, setShops] = useState<Shop[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { mode, selectedCountry } = useExperienceStore();
  const viewport = useThree((state) => state.viewport);
  const camera = useThree((state) => state.camera);

  // Entrance animation logic
  const entryProgress = useRef(0);
  const scrollY = useRef(0);
  const targetScrollY = useRef(0);

  useEffect(() => {
    if (mode === 'HUB') {
      entryProgress.current = 0;
      scrollY.current = 0;
      targetScrollY.current = 0;
      sounds.play('whoosh', 0.4);
      gsap.to(entryProgress, {
        current: 1,
        duration: 1.2,
        ease: 'power3.out'
      });
    }
  }, [mode]);

  // Handle Wheel & Drag Events for Scrolling
  useEffect(() => {
    let isDragging = false;
    let lastY = 0;

    const handleWheel = (e: WheelEvent) => {
      if (mode !== 'HUB') return;
      targetScrollY.current -= e.deltaY * 0.05;
      clampScroll();
    };

    const handleMouseDown = (e: MouseEvent) => {
      isDragging = true;
      lastY = e.clientY;
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || mode !== 'HUB') return;
      const delta = e.clientY - lastY;
      targetScrollY.current += delta * 0.05;
      lastY = e.clientY;
      clampScroll();
    };

    const handleMouseUp = () => {
      isDragging = false;
    };

    const clampScroll = () => {
      const rows = Math.ceil(shops.length / 3);
      const minScroll = -((rows - 1) * 10);
      targetScrollY.current = Math.min(20, Math.max(minScroll, targetScrollY.current));
    };

    window.addEventListener('wheel', handleWheel);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [mode, shops.length]);

  useEffect(() => {
    const fetchShops = async () => {
      setError(null);
      try {
        const q = query(collection(db, "shops"), limit(50)); 
        const snap = await getDocs(q);
        let allShops = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Shop);
        
        // Filter by country if selected
        if (selectedCountry) {
           const CANONICAL_MAP: Record<string, string> = {
             'estados unidos': 'usa',
             'usa': 'usa',
             'reino unido': 'uk',
             'uk': 'uk',
             'japon': 'japón',
             'japón': 'japón',
             'espana': 'españa',
             'españa': 'españa'
           };
           
           const target = selectedCountry.toLowerCase().trim();
           const canonicalTarget = CANONICAL_MAP[target] || target;

           allShops = allShops.filter(s => {
             const countryRaw = s.location?.country?.toLowerCase().trim();
             if (!countryRaw) return false;
             const countryCanonical = CANONICAL_MAP[countryRaw] || countryRaw;
             return countryCanonical === canonicalTarget;
           });
         }
        
        setShops(allShops);
      } catch (e) {
        console.error("Error fetching shops for 3D Hub:", e);
        setError("Error de conexión con el servidor. Reintentando...");
        try {
          handleFirestoreError(e, OperationType.LIST, "shops");
        } catch (err) {
          // Error already logged by handleFirestoreError
        }
      }
    };
    fetchShops();
  }, [selectedCountry]);

  // Cube Layout Rotation / Smooth Scroll Lerping
  useFrame((state, delta) => {
    // Smooth Lerp scroll
    scrollY.current = THREE.MathUtils.lerp(scrollY.current, targetScrollY.current, 0.1);
  });

  if (mode !== 'HUB') return null;

  return (
    <group>
      <fog attach="fog" args={['#000000', 40, 150]} />
      
      {/* Error Message */}
      {error && (
        <Text
          position={[0, 0, -20]}
          fontSize={1.5}
          color="#ff4444"
          maxWidth={15}
          textAlign="center"
        >
          {error}
        </Text>
      )}

      {/* Hero Title */}
      <Text
        position={[0, 25, -60]}
        fontSize={18}
        color="white"
        fillOpacity={0.05}
        fontWeight="black"
        letterSpacing={-0.05}
      >
        EmpreMall
      </Text>

      {/* Shop Formation (Scrollable Grid) */}
      <group 
        position={[0, 0, -35]}
        ref={(el) => {
          if (el) {
            el.position.z = -120 + (entryProgress.current * 85);
            el.scale.setScalar(0.5 + (entryProgress.current * 0.5));
          }
        }}
      >
        <group position={[0, scrollY.current, 0]}>
          {shops.map((shop, i) => {
            const cols = 3;
            const spacingX = 8;
            const spacingY = 10;
            
            const colIdx = i % cols;
            const rowIdx = Math.floor(i / cols);
            
            const x = (colIdx - (cols - 1) / 2) * spacingX;
            const y = -rowIdx * spacingY;
            const z = Math.abs(x) * 0.2; // Subtle curve

            return (
              <StorePod 
                key={shop.id} 
                shop={shop} 
                position={[x, y, z]} 
                index={i} 
                rotation={[0, -x * 0.05, 0]}
              />
            );
          })}
        </group>
      </group>
      
      {/* Lighting elements adapted for new layout */}
      <pointLight position={[20, 20, 20]} intensity={1} color="#ffffff" />
      <pointLight position={[-20, -20, -10]} intensity={0.5} color="#ffffff" />
      <Sparkles count={100} scale={50} size={1} speed={0.2} opacity={0.1} color="#ffffff" />
      
      {/* Ground subtle reflection */}
      <mesh position={[0, -25, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[200, 200]} />
        <MeshReflectorMaterial
          blur={[300, 100]}
          resolution={512}
          mixBlur={1}
          mixStrength={15}
          roughness={1}
          depthScale={1.2}
          minDepthThreshold={0.4}
          maxDepthThreshold={1.4}
          color="#050505"
          metalness={0.5}
          transparent
          opacity={0.5}
        />
      </mesh>
    </group>
  );
};
